/**
 * Каталог пряников — ИИ-сцена набора «на торте» (этап 6, CONTRACT §5).
 *
 * `POST /gingerbread/kits/:article/scene` без `quote` отвечает сметой, с `quote` и `Idempotency-Key` ставит платную
 * операцию в очередь (202 `{runId, operationId}`); `GET` отдаёт состояние сцены. ИИ рисует только фон (торт сверху),
 * пряники вклеивает ПИМ — слайд 4 «На торте» появляется при следующем рендере слайдов набора. Ответы разбираются здесь,
 * в одном месте, и устойчивы к лишним полям и неизвестным статусам.
 */

import type { BadgeVariant } from '../../components/ui/Badge'

export type SceneStatus = 'none' | 'running' | 'ready' | 'failed'

export interface SceneProblem {
  code: string
  message: string
}

export interface KitScene {
  status: SceneStatus
  /** Защищённый адрес превью готовой сцены (грузится авторизованным запросом); у неудачной новой попытки остаётся прежняя сцена. */
  previewUrl: string | null
  operationId: string | null
  createdAt: string | null
  warnings: SceneProblem[]
  error: SceneProblem | null
}

export interface SceneQuote {
  estimateUsd: number
  quote: string | null
  quoteExpiresAt: string | null
  blockers: SceneProblem[]
  /** У набора уже есть готовая сцена: новая заменит её. */
  replacesScene: boolean
  itemsCount: number | null
  budget: { limitUsd: number; availableUsd: number } | null
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
const text = (value: unknown): string | null => (value == null || value === '' ? null : String(value))
const num = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const SCENE_STATUSES: SceneStatus[] = ['none', 'running', 'ready', 'failed']

function toProblem(raw: unknown): SceneProblem | null {
  if (typeof raw === 'string') return raw ? { code: '', message: raw } : null
  const value = asRecord(raw)
  const code = text(value.code) ?? ''
  const message = text(value.message) ?? ''
  return code || message ? { code, message } : null
}

const toProblems = (raw: unknown): SceneProblem[] =>
  (Array.isArray(raw) ? raw : []).map(toProblem).filter((entry): entry is SceneProblem => entry != null)

/** Разбор `GET .../scene`. Неизвестный статус не показываем наугад: есть превью — «готова», иначе «нет». */
export function toKitScene(raw: unknown): KitScene {
  const value = asRecord(raw)
  const declared = String(value.status ?? '').toLowerCase() as SceneStatus
  const previewUrl = text(value.previewUrl)
  const status: SceneStatus = SCENE_STATUSES.includes(declared) ? declared : previewUrl ? 'ready' : 'none'
  return {
    status,
    previewUrl,
    operationId: text(value.operationId),
    createdAt: text(value.createdAt),
    warnings: toProblems(value.warnings),
    error: toProblem(value.error),
  }
}

/** Разбор сметы (`POST` без `quote`). */
export function toSceneQuote(raw: unknown): SceneQuote {
  const value = asRecord(raw)
  const budget = asRecord(value.budget)
  const limit = num(budget.limitUsd)
  const available = num(budget.availableUsd)
  return {
    estimateUsd: num(value.estimateUsd) ?? 0,
    quote: text(value.quote),
    quoteExpiresAt: text(value.quoteExpiresAt),
    blockers: toProblems(value.blockers),
    replacesScene: value.replacesScene === true,
    itemsCount: num(value.itemsCount),
    budget: limit != null && available != null ? { limitUsd: limit, availableUsd: available } : null,
  }
}

/** Операция поставлена: `operationId` из ответа 202. */
export function toSceneStart(raw: unknown): { runId: string | null; operationId: string | null } {
  const value = asRecord(raw)
  return { runId: text(value.runId), operationId: text(value.operationId) }
}

// ── Подписи ─────────────────────────────────────────────────────────

export const SCENE_STATUS_LABEL: Record<SceneStatus, string> = {
  none: 'Нет сцены',
  running: 'Рисуется',
  ready: 'Готова',
  failed: 'Ошибка',
}

export const SCENE_STATUS_VARIANT: Record<SceneStatus, BadgeVariant> = {
  none: 'gray',
  running: 'blue',
  ready: 'green',
  failed: 'red',
}

/** Предупреждения media о сцене, которые стоит объяснить по-русски. */
export const SCENE_WARNING_HINTS: Record<string, string> = {
  SCENE_CAKE_NOT_FOUND: 'Торт на сцене не найден автоматически: пряники уложены в центр по умолчанию. Если выглядит неудачно, нарисуйте сцену заново.',
}

/** «0,20 $»: доллары с запятой и пробелом перед знаком. */
export const formatSceneUsd = (value: number): string =>
  `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(value)} $`

/** Ошибки гейтвея, после которых смета недействительна и её нужно рассчитать заново. */
export const SCENE_QUOTE_ERROR_CODES: ReadonlySet<string> = new Set(['QUOTE_INVALID', 'QUOTE_CONFLICT', 'QUOTE_EXPIRED'])

/** Текст подтверждения суммы перед платным запуском. */
export function sceneConfirmMessage(quote: Pick<SceneQuote, 'estimateUsd' | 'replacesScene'>): string {
  const lines = [
    `ИИ нарисует фон сцены: круглый торт сверху, без пряников и без текста. Запуск спишет с бюджета задания до ${formatSceneUsd(quote.estimateUsd)}; факт может оказаться меньше.`,
    'Пряники набора ПИМ вклеит сам, рисунок на пряниках не меняется. После готовности слайды и черновики карточек набора пересоздадутся автоматически, в «Черновиках ПИМ» появится слайд «На торте». Публикации на маркетплейсы не будет.',
  ]
  if (quote.replacesScene) lines.push('Прежняя сцена набора будет заменена новой.')
  return lines.join('\n\n')
}
